import {add as dateAdd, sub as dateSubtract} from 'date-fns';
import Config from 'react-native-config';
import * as KeyCommand from 'react-native-key-command';
import {getDeviceTimezone} from './libs/TimezoneUtils';
import SCREENS from './SCREENS';

// Creating a default array and object this way because objects ({}) and arrays ([]) are not stable types.
// Freezing the array ensures that it cannot be unintentionally modified.
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});
const CURRENT_YEAR = new Date().getFullYear();
const PLATFORM_OS_MACOS = 'Mac OS';
const PLATFORM_IOS = 'iOS';
const ANDROID_PACKAGE_NAME = 'com.alcohol_tracker';
const KIROKU_URL = 'https://kiroku.cz';
// Custom sender domains for Firebase Authentication transactional mail
// (email verification, password reset, email-change confirmation). Configured
// in the Firebase Console per project with matching DNS records (DKIM/SPF).
// Subdomain isolates transactional mail reputation from the apex domain.
const TRANSACTIONAL_MAIL_DOMAINS = {
  DEV: 'mail.dev.kiroku.cz',
  PROD: 'mail.kiroku.cz',
};
const TRANSACTIONAL_MAIL_DOMAIN =
  Config?.ENVIRONMENT === 'production'
    ? TRANSACTIONAL_MAIL_DOMAINS.PROD
    : TRANSACTIONAL_MAIL_DOMAINS.DEV;
const PULL_REQUEST_NUMBER = Config?.PULL_REQUEST_NUMBER ?? '';
const MAX_DATE = dateAdd(new Date(), {years: 1});
const MIN_DATE = dateSubtract(new Date(), {years: 20});

const keyModifierControl =
  KeyCommand?.constants?.keyModifierControl ?? 'keyModifierControl';
const keyModifierCommand =
  KeyCommand?.constants?.keyModifierCommand ?? 'keyModifierCommand';
const keyModifierShiftControl =
  KeyCommand?.constants?.keyModifierShiftControl ?? 'keyModifierShiftControl';
const keyModifierShiftCommand =
  KeyCommand?.constants?.keyModifierShiftCommand ?? 'keyModifierShiftCommand';
const keyInputEscape =
  KeyCommand?.constants?.keyInputEscape ?? 'keyInputEscape';
const keyInputEnter = KeyCommand?.constants?.keyInputEnter ?? 'keyInputEnter';
const keyInputUpArrow =
  KeyCommand?.constants?.keyInputUpArrow ?? 'keyInputUpArrow';
const keyInputDownArrow =
  KeyCommand?.constants?.keyInputDownArrow ?? 'keyInputDownArrow';
const keyInputLeftArrow =
  KeyCommand?.constants?.keyInputLeftArrow ?? 'keyInputLeftArrow';
const keyInputRightArrow =
  KeyCommand?.constants?.keyInputRightArrow ?? 'keyInputRightArrow';

// describes if a shortcut key can cause navigation
const KEYBOARD_SHORTCUT_NAVIGATION_TYPE = 'NAVIGATION_SHORTCUT';

/** Drinking session types */
const SESSION_TYPES = {
  LIVE: 'live',
  EDIT: 'edit',
} as const;

const CONST = {
  ANDROID_PACKAGE_NAME,
  ANIMATED_TRANSITION: 300,
  ANIMATED_TRANSITION_FROM_VALUE: 100,
  // Safety-net upper bound for a screen's entry transition. Deferred heavy
  // content flips on at `transitionEnd`; this only matters if that event never
  // fires for a given platform/modal presentation.
  SCREEN_TRANSITION_END_TIMEOUT: 1000,
  ANIMATION_IN_TIMING: 100,
  ANIMATION_DIRECTION: {
    IN: 'in',
    OUT: 'out',
  },
  APP_DOWNLOAD_LINK: `${KIROKU_URL}/get`,
  APP_URLS: {
    DEV: 'https://dev.kiroku.cz',
    STAGING: 'https://staging.kiroku.cz',
    PROD: KIROKU_URL,
    ADHOC: 'https://adhoc.kiroku.cz',
    TEST: 'https://test.kiroku.cz',
  },
  KIROKU_URL,
  GITHUB_URL: 'https://github.com/PetrCala/Kiroku',
  DISCORD_INVITE_URL: 'https://discord.gg/PjvUFkuwMv',
  TERMS_URL: `${KIROKU_URL}/terms`,
  PRIVACY_URL: `${KIROKU_URL}/privacy`,
  SUPPORT_URL: `${KIROKU_URL}/support`,
  SUBSCRIPTION_TERMS_URL: `${KIROKU_URL}/subscription-terms`,
  CURRENT_TERMS_VERSION: 1,
  GITHUB_RELEASE_URL:
    'https://api.github.com/repos/PetrCala/Kiroku/releases/latest',
  ADMIN_SDK: {
    DEV: 'kiroku-admin-sdk-dev',
    PROD: 'kiroku-admin-sdk-prod',
  },
  API_REQUEST_TYPE: {
    READ: 'read',
    WRITE: 'write',
    MAKE_REQUEST_WITH_SIDE_EFFECTS: 'makeRequestWithSideEffects',
  },
  AUTO_AUTH_STATE: {
    NOT_STARTED: 'not-started',
    SIGNING_IN: 'signing-in',
    JUST_SIGNED_IN: 'just-signed-in',
    FAILED: 'failed',
  },
  APP_STATE: {
    ACTIVE: 'active',
    BACKGROUND: 'background',
    INACTIVE: 'inactive',
  },
  APP_IN_BETA: true,
  // Compile-time feature-flag defaults. Read through `FeatureFlags.isEnabled`
  // or `useFeatureFlag`, never directly: a remote override in the global config
  // (`config/feature_flags/<FLAG>`) wins over these. See
  // contributingGuides/FEATURE_FLAGS.md.
  FEATURES: {
    BADGES: false,
    // Boot-splash logo fly-in / splash→logo handoff (#1196). Off so the in-app
    // logo plays its full assembly + liquid-fill entrance instead of being
    // masked by the flying splash logo.
    LOGO_FLY_IN: false,
    // Session writes as small idempotent ops (Sessions v2, RFC §5). Off until
    // the server implements more than the `ping` shell.
    SESSION_OPS: false,
    // New sessions are written as Sessions v2 (RFC §4): `schema_version: 2`, a
    // default name, `visibility`, and drinks as `entries` instead of buckets.
    // Off until the backfill has run and the server accepts schema 2
    // (Kiroku#1664). Old sessions read through the adapter either way.
    SESSIONS_V2_SCHEMA: false,
  },
  // Session op types (Sessions v2 RFC §5.1). Mirrors kiroku-api
  // `lib/sessions/ops.ts`, which rejects anything else.
  SESSION_OP: {
    TYPE: {
      PING: 'ping',
      START: 'start',
      END: 'end',
      ADD_ENTRY: 'add_entry',
      EDIT_ENTRY: 'edit_entry',
      DELETE_ENTRY: 'delete_entry',
      ADD_ROUND: 'add_round',
      DECLINE_ENTRY: 'decline_entry',
      CLAIM_ENTRY: 'claim_entry',
      SET_NOTE: 'set_note',
      SET_BLACKOUT: 'set_blackout',
      SET_VISIBILITY: 'set_visibility',
      RENAME: 'rename',
      ADD_PHOTO: 'add_photo',
      REMOVE_PHOTO: 'remove_photo',
      CLOSE: 'close',
      REMOVE_MEMBER: 'remove_member',
      TRANSFER_ADMIN: 'transfer_admin',
      SET_MEMBERS_CAN_INVITE: 'set_members_can_invite',
      LEAVE: 'leave',
      CREATE_JOIN_CODE: 'create_join_code',
      INVITE: 'invite',
      JOIN: 'join',
      RESPOND_INVITE: 'respond_invite',
    },
  },
  // Registry of features gated behind a subscription tier. Read via the pure
  // `getFeatureAccess` resolver (`@libs/Entitlements`) and the `useFeatureAccess`
  // hook — never branch on this object directly at call sites.
  //
  // - `tier`: `'free'` is always unlocked; `'plus'` is locked only when the
  //   premium gates are active (dev/staging today, production at v1.1) AND the
  //   user is not a supporter.
  // - `availabilityFlag` (optional): a `CONST.FEATURES` key. When that flag is
  //   off the feature is treated as not-in-build (unavailable), which wins over
  //   every gate, including the dev override.
  PREMIUM_FEATURES: {
    CUSTOM_COLOR_PALETTE: {tier: 'plus'},
  },
  // Tip-jar consumable IAP product ids, cheapest first: the order here is the
  // display order on the Support screen. Must stay in step with the products
  // created in App Store Connect by `scripts/asc-tips.mjs`, because StoreKit
  // silently omits any id it does not recognize. A tip unlocks nothing; see
  // contributingGuides/TIP_JAR.md. `kiroku.tipjar.pint` is the middle tier,
  // shown as "Beer" since the rename: ids are burn-once, so the old spelling
  // stays in the id forever and no user ever sees it.
  TIPS: {
    PRODUCT_IDS: [
      'kiroku.tipjar.small_beer',
      'kiroku.tipjar.pint',
      'kiroku.tipjar.round',
    ],
  },
  APP_UPDATE: {
    // How long the dismiss update button should hide the window for
    DISMISS_TIME: 1000 * 60 * 60 * 24 * 1, // 1 day
  },
  /** The one gated ask for the tip jar on Home; see contributingGuides/TIP_JAR.md. */
  TIP_JAR_PROMPT: {
    /** The app must have been used on this device for this long before the card can appear. */
    MIN_MS_SINCE_FIRST_OPEN: 1000 * 60 * 60 * 24 * 14, // 14 days
    /** The user must have this many completed sessions before the card can appear. */
    MIN_COMPLETED_SESSIONS: 5,
    /** The card appears at most this many times per device, ever. */
    MAX_IMPRESSIONS: 3,
    /** The floor between two impressions; also what "Not now" hides the card for. */
    COOLDOWN_MS: 1000 * 60 * 60 * 24 * 120, // 120 days
  },
  AUTH_TOKEN_TYPES: {
    ANONYMOUS: 'anonymousAccount',
    SUPPORT: 'support',
  },
  AVAILABLE_PLATFORMS: ['ios', 'android'],
  ACTIVITY_INDICATOR_SIZE: {
    SMALL: 'small',
    LARGE: 'large',
  },
  AVATAR_SIZE: {
    XLARGE: 'xlarge',
    LARGE: 'large',
    MEDIUM: 'medium',
    DEFAULT: 'default',
    SMALL: 'small',
    SMALLER: 'smaller',
    SUBSCRIPT: 'subscript',
    SMALL_SUBSCRIPT: 'small-subscript',
    MID_SUBSCRIPT: 'mid-subscript',
    LARGE_BORDERED: 'large-bordered',
    HEADER: 'header',
    MENTION_ICON: 'mention-icon',
    SMALL_NORMAL: 'small-normal',
  },
  AVATAR_ROW_SIZE: {
    DEFAULT: 4,
    LARGE_SCREEN: 8,
  },
  BROWSER: {
    CHROME: 'chrome',
    FIREFOX: 'firefox',
    IE: 'ie',
    EDGE: 'edge',
    Opera: 'opera',
    SAFARI: 'safari',
    OTHER: 'other',
  },
  BUTTON_STATES: {
    DEFAULT: 'default',
    ACTIVE: 'active',
    PRESSED: 'pressed',
    COMPLETE: 'complete',
    DISABLED: 'disabled',
  },
  CALENDAR_COLORS: {
    LIGHT: {
      YELLOW: 'yellow',
      ORANGE: 'orange',
    },
    DARK: {
      RED: 'red',
      BLACK: 'black',
      GREEN: 'green',
    },
    TEXT: {
      WHITE: 'white',
      BLACK: 'black',
    },
  },

  CALENDAR_PICKER: {
    // Numbers were arbitrarily picked.
    MIN_YEAR: CURRENT_YEAR - 100,
    MAX_YEAR: CURRENT_YEAR + 100,
    MAX_DATE,
    MIN_DATE,
  },
  COLOR_SCHEME: {
    LIGHT: 'light',
    DARK: 'dark',
  },
  CONFIRM_CONTENT_SVG_SIZE: {
    HEIGHT: 220,
    WIDTH: 130,
  },
  COUNTRY: {
    US: 'US',
    CZ: 'CZ',
  },
  CUSTOM_STATUS_TYPES: {
    NEVER: 'never',
    THIRTY_MINUTES: 'thirtyMinutes',
    ONE_HOUR: 'oneHour',
    AFTER_TODAY: 'afterToday',
    AFTER_WEEK: 'afterWeek',
    CUSTOM: 'custom',
  },
  DATE: {
    SQL_DATE_TIME: 'YYYY-MM-DD HH:mm:ss',
    FNS_FORMAT_STRING: 'yyyy-MM-dd',
    LOCAL_TIME_FORMAT: 'h:mm a',
    SHORT_TIME_FORMAT: 'HH:mm',
    YEAR_MONTH_FORMAT: 'yyyyMM',
    MONTH_FORMAT: 'MMMM',
    SHORT_MONTH_FORMAT: 'LLL',
    WEEKDAY_TIME_FORMAT: 'eeee',
    MONTH_DAY_ABBR_FORMAT: 'MMM d',
    SHORT_DATE_FORMAT: 'MM-dd',
    MONTH_DAY_YEAR_ABBR_FORMAT: 'MMM d, yyyy',
    MONTH_DAY_YEAR_FORMAT: 'MMMM d, yyyy',
    MONTH_YEAR_ABBR_FORMAT: 'MMM yyyy',
    FNS_TIMEZONE_FORMAT_STRING: "yyyy-MM-dd'T'HH:mm:ssXXX",
    FNS_DB_FORMAT_STRING: 'yyyy-MM-dd HH:mm:ss.SSS',
    LONG_DATE_FORMAT_WITH_WEEKDAY: 'eeee, MMMM d, yyyy',
    CALENDAR_FORMAT: 'yyyy-MM-dd',
    UNIX_EPOCH: '1970-01-01 00:00:00.000',
    MAX_DATE: '9999-12-31',
    MIN_DATE: '0001-01-01',
    ORDINAL_DAY_OF_MONTH: 'do',
  },

  TIME_UNITS: {
    SECOND: 'second',
    MINUTE: 'minute',
    HOUR: 'hour',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    YEAR: 'year',
  },

  DATE_BIRTH: {
    MIN_AGE: 18,
    MAX_AGE: 150,
  },

  DEBUG_CONSOLE: {
    LEVELS: {
      INFO: 'INFO',
      ERROR: 'ERROR',
      RESULT: 'RESULT',
      DEBUG: 'DEBUG',
    },
  },
  DEEPLINK_BASE_URL: 'kiroku://',

  FRIEND_INVITE: {
    /**
     * Personal invite links. They open the web app, or the native app through
     * iOS universal links / Android app links (see `web/.well-known`).
     */
    LINK_BASE_URL: 'https://app.kiroku.cz/add/',
    /** How long a link opened while signed out waits for sign-in to finish. */
    PENDING_TTL_MS: 24 * 60 * 60 * 1000,
  },

  /** Tabs of the Add friends hub (`social/add-friends?tab=`). */
  ADD_FRIENDS_TAB: {
    CODE: 'code',
    SEARCH: 'search',
  },
  DEFAULT_AVATAR_COUNT: 24,
  DIRECTION: {
    LEFT: 'left',
    RIGHT: 'right',
  },
  DISPLAY_NAME: {
    RESERVED_NAMES: ['Kiroku'],
  },
  DROPDOWN_BUTTON_SIZE: {
    LARGE: 'large',
    MEDIUM: 'medium',
  },
  ENVIRONMENT: {
    DEV: 'development',
    ADHOC: 'adhoc',
    PROD: 'production',
    STAGING: 'staging',
    TEST: 'test',
  },
  LHN_SKELETON_VIEW_ITEM_HEIGHT: 64,
  SEARCH_SKELETON_VIEW_ITEM_HEIGHT: 108,
  EMAIL: {
    KIROKU: 'kiroku.alcohol.tracker@gmail.com',
    KIROKU_EMAIL_DOMAIN: '@kiroku.cz',
    TRANSACTIONAL_MAIL_DOMAIN,
    TRANSACTIONAL_MAIL_DOMAINS,
    TRANSACTIONAL_NOREPLY_ADDRESS: `noreply@${TRANSACTIONAL_MAIL_DOMAIN}`,
  },
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  ERROR: {
    XHR_FAILED: 'xhrFailed',
    THROTTLED: 'throttled',
    UNKNOWN_ERROR: 'Unknown error',
    REQUEST_CANCELLED: 'AbortError',
    FAILED_TO_FETCH: 'Failed to fetch',
    ENSURE_BUGBOT: 'ENSURE_BUGBOT',
    PUSHER_ERROR: 'PusherError',
    WEB_SOCKET_ERROR: 'WebSocketError',
    NETWORK_REQUEST_FAILED: 'Network request failed',
    SAFARI_DOCUMENT_LOAD_ABORTED: 'cancelled',
    FIREFOX_DOCUMENT_LOAD_ABORTED:
      'NetworkError when attempting to fetch resource.',
    IOS_NETWORK_CONNECTION_LOST: 'The network connection was lost.',
    IOS_NETWORK_CONNECTION_LOST_CZECH: 'Spojení bylo ztraceno.',
    IOS_LOAD_FAILED: 'Load failed',
    SAFARI_CANNOT_PARSE_RESPONSE: 'cannot parse response',
    GATEWAY_TIMEOUT: 'Gateway Timeout',
    KIROKU_SERVICE_INTERRUPTED: 'Kiroku service interrupted',
    DUPLICATE_RECORD: 'A record already exists with this ID',
  },
  EVENTS: {
    SCROLLING: 'scrolling',
  },

  EXCLUDE_FROM_LAST_VISITED_PATH: [
    SCREENS.NOT_FOUND,
    // SCREENS.SAML_SIGN_IN,
  ] as string[],

  // Character Limits
  FORM_CHARACTER_LIMIT: 50,
  LEGAL_NAMES_CHARACTER_LIMIT: 150,
  LOGIN_CHARACTER_LIMIT: 254,
  SESSION_NOTE_CHARACTER_LIMIT: 256,
  CATEGORY_NAME_LIMIT: 256,
  TAG_NAME_LIMIT: 256,
  TITLE_CHARACTER_LIMIT: 100,
  DESCRIPTION_LIMIT: 1000,
  SESSION_NAME_CHARACTER_LIMIT: 80,

  // Default window for the initial Firebase RTDB fetch of a user's drinking
  // sessions. The home calendar starts by streaming only the last N months
  // and extends the window when the user scrolls back past the loaded edge.
  SESSIONS_INITIAL_FETCH_MONTHS: 3,

  // Debounce window (ms) for persisting the calendar's scroll depth. Coalesces
  // rapid left-arrow scrolls into a single Firebase listener resubscribe /
  // friend-profile refetch at the end of the run. Long enough to absorb a
  // burst, short enough to feel responsive.
  SESSIONS_CALENDAR_PERSIST_DEBOUNCE_MS: 150,

  FRIEND_REQUEST_STATUS: {
    SELF: 'self',
    SENT: 'sent',
    RECEIVED: 'received',
    FRIEND: 'friend',
    UNDEFINED: 'undefined',
  },
  HTTP_STATUS: {
    // The Firebase ID token was revoked/disabled/invalid (NOT merely expired —
    // that is `JSON_CODE.NOT_AUTHENTICATED` 407, which the Reauthentication
    // middleware refreshes and replays). A 401 means a token refresh cannot
    // recover the session, so the client force-signs-out (see HttpUtils).
    UNAUTHORIZED: 401,
    // Validation failures, e.g. redeeming your own invite link.
    BAD_REQUEST: 400,
    // Neutral refusal, e.g. a block between two users (reason never disclosed).
    FORBIDDEN: 403,
    // No such route or resource (e.g. an unknown invite code). Also what the
    // retired legacy `{root}api/{Command}` host answered, so HttpUtils reuses it
    // for an unrouted command (droppable).
    NOT_FOUND: 404,
    // The resource already exists — e.g. provisioning an already-provisioned
    // user. Expected/benign for idempotent retries, not a service failure.
    CONFLICT: 409,
    // The request took too long (a proxy or load balancer gave up on it).
    // Transient: the queue retries it rather than dropping it.
    REQUEST_TIMEOUT: 408,
    // The server won't process the request yet. Transient, like 408.
    TOO_EARLY: 425,
    // When Cloudflare throttles
    TOO_MANY_REQUESTS: 429,
    INTERNAL_SERVER_ERROR: 500,
    BAD_GATEWAY: 502,
    GATEWAY_TIMEOUT: 504,
    UNKNOWN_ERROR: 520,
  },

  MISSING_TRANSLATION: 'MISSING TRANSLATION',
  LANGUAGES: ['en', 'cs_cz'],

  SEARCH: {
    RESULTS_PAGE_SIZE: 50,
    // Minimum normalized-prefix length for a `nickname_to_id` range query.
    // Shorter prefixes are rejected to avoid scanning most of the index.
    MIN_NICKNAME_PREFIX_LENGTH: 2,
    // Upper bound on token buckets pulled for a single prefix range query.
    NICKNAME_MAX_RESULTS: 50,
    // Upper bound on token entries written per user, to cap index fan-out.
    NICKNAME_MAX_TOKENS: 8,
    DATA_TYPES: {
      SESSION: 'session',
    },
    ACTION_TYPES: {
      VIEW: 'view',
    },
    SORT_ORDER: {
      ASC: 'asc',
      DESC: 'desc',
    },
    STATUS: {
      SESSION: {
        ALL: 'all',
        FINISHED: 'finished',
      },
    },
    TABLE_COLUMNS: {
      DATE: 'date',
      DESCRIPTION: 'description',
      FROM: 'from',
      TO: 'to',
      CATEGORY: 'category',
      TAG: 'tag',
      TYPE: 'type',
      ACTION: 'action',
    },
    SYNTAX_OPERATORS: {
      AND: 'and',
      OR: 'or',
      EQUAL_TO: 'eq',
      NOT_EQUAL_TO: 'neq',
      GREATER_THAN: 'gt',
      GREATER_THAN_OR_EQUAL_TO: 'gte',
      LOWER_THAN: 'lt',
      LOWER_THAN_OR_EQUAL_TO: 'lte',
    },
    SYNTAX_ROOT_KEYS: {
      TYPE: 'type',
      STATUS: 'status',
      SORT_BY: 'sortBy',
      SORT_ORDER: 'sortOrder',
    },
    SYNTAX_FILTER_KEYS: {
      DATE: 'date',
      AMOUNT: 'amount',
      FROM: 'from',
      TO: 'to',
      CATEGORY: 'category',
      TAG: 'tag',
      KEYWORD: 'keyword',
      IN: 'in',
    },
  },

  DESKTOP_HEADER_PADDING: 12,

  ICON_TYPE_ICON: 'icon',
  ICON_TYPE_AVATAR: 'avatar',
  INVALID_CHARS: ['.', '#', '$', '[', ']'],
  JSON_CODE: {
    SUCCESS: 200,
    BAD_REQUEST: 400,
    NOT_AUTHENTICATED: 407,
    EXP_ERROR: 666,
    MANY_WRITES_ERROR: 665,
    UNABLE_TO_RETRY: 'unableToRetry',
    UPDATE_REQUIRED: 426,
  },
  PLATFORM_SPECIFIC_KEYS: {
    CTRL: {
      DEFAULT: 'control',
      [PLATFORM_OS_MACOS]: 'meta',
      [PLATFORM_IOS]: 'meta',
    },
    SHIFT: {
      DEFAULT: 'shift',
    },
  },
  ENVIRONMENT_SUFFIX: {
    DEV: ' Dev',
    ADHOC: ' AdHoc',
  },
  KEYBOARD_SHORTCUTS: {
    SEARCH: {
      descriptionKey: 'search',
      shortcutKey: 'K',
      modifiers: ['CTRL'],
      trigger: {
        DEFAULT: {input: 'k', modifierFlags: keyModifierControl},
        [PLATFORM_OS_MACOS]: {input: 'k', modifierFlags: keyModifierCommand},
        [PLATFORM_IOS]: {input: 'k', modifierFlags: keyModifierCommand},
      },
      type: KEYBOARD_SHORTCUT_NAVIGATION_TYPE,
    },
    NEW_CHAT: {
      descriptionKey: 'newChat',
      shortcutKey: 'K',
      modifiers: ['CTRL', 'SHIFT'],
      trigger: {
        DEFAULT: {input: 'k', modifierFlags: keyModifierShiftControl},
        [PLATFORM_OS_MACOS]: {
          input: 'k',
          modifierFlags: keyModifierShiftCommand,
        },
        [PLATFORM_IOS]: {input: 'k', modifierFlags: keyModifierShiftCommand},
      },
      type: KEYBOARD_SHORTCUT_NAVIGATION_TYPE,
    },
    SHORTCUTS: {
      descriptionKey: 'openShortcutDialog',
      shortcutKey: 'J',
      modifiers: ['CTRL'],
      trigger: {
        DEFAULT: {input: 'j', modifierFlags: keyModifierControl},
        [PLATFORM_OS_MACOS]: {input: 'j', modifierFlags: keyModifierCommand},
        [PLATFORM_IOS]: {input: 'j', modifierFlags: keyModifierCommand},
      },
    },
    ESCAPE: {
      descriptionKey: 'escape',
      shortcutKey: 'Escape',
      modifiers: [],
      trigger: {
        DEFAULT: {input: keyInputEscape},
        [PLATFORM_OS_MACOS]: {input: keyInputEscape},
        [PLATFORM_IOS]: {input: keyInputEscape},
      },
    },
    ENTER: {
      descriptionKey: null,
      shortcutKey: 'Enter',
      modifiers: [],
      trigger: {
        DEFAULT: {input: keyInputEnter},
        [PLATFORM_OS_MACOS]: {input: keyInputEnter},
        [PLATFORM_IOS]: {input: keyInputEnter},
      },
    },
    CTRL_ENTER: {
      descriptionKey: null,
      shortcutKey: 'Enter',
      modifiers: ['CTRL'],
      trigger: {
        DEFAULT: {input: keyInputEnter, modifierFlags: keyModifierControl},
        [PLATFORM_OS_MACOS]: {
          input: keyInputEnter,
          modifierFlags: keyModifierCommand,
        },
        [PLATFORM_IOS]: {
          input: keyInputEnter,
          modifierFlags: keyModifierCommand,
        },
      },
    },
    COPY: {
      descriptionKey: 'copy',
      shortcutKey: 'C',
      modifiers: ['CTRL'],
      trigger: {
        DEFAULT: {input: 'c', modifierFlags: keyModifierControl},
        [PLATFORM_OS_MACOS]: {input: 'c', modifierFlags: keyModifierCommand},
        [PLATFORM_IOS]: {input: 'c', modifierFlags: keyModifierCommand},
      },
    },
    ARROW_UP: {
      descriptionKey: null,
      shortcutKey: 'ArrowUp',
      modifiers: [],
      trigger: {
        DEFAULT: {input: keyInputUpArrow},
        [PLATFORM_OS_MACOS]: {input: keyInputUpArrow},
        [PLATFORM_IOS]: {input: keyInputUpArrow},
      },
    },
    ARROW_DOWN: {
      descriptionKey: null,
      shortcutKey: 'ArrowDown',
      modifiers: [],
      trigger: {
        DEFAULT: {input: keyInputDownArrow},
        [PLATFORM_OS_MACOS]: {input: keyInputDownArrow},
        [PLATFORM_IOS]: {input: keyInputDownArrow},
      },
    },
    ARROW_LEFT: {
      descriptionKey: null,
      shortcutKey: 'ArrowLeft',
      modifiers: [],
      trigger: {
        DEFAULT: {input: keyInputLeftArrow},
        [PLATFORM_OS_MACOS]: {input: keyInputLeftArrow},
        [PLATFORM_IOS]: {input: keyInputLeftArrow},
      },
    },
    ARROW_RIGHT: {
      descriptionKey: null,
      shortcutKey: 'ArrowRight',
      modifiers: [],
      trigger: {
        DEFAULT: {input: keyInputRightArrow},
        [PLATFORM_OS_MACOS]: {input: keyInputRightArrow},
        [PLATFORM_IOS]: {input: keyInputRightArrow},
      },
    },
    TAB: {
      descriptionKey: null,
      shortcutKey: 'Tab',
      modifiers: [],
    },
  },
  KEYBOARD_SHORTCUTS_TYPES: {
    NAVIGATION_SHORTCUT: KEYBOARD_SHORTCUT_NAVIGATION_TYPE,
  },
  KEYBOARD_SHORTCUT_KEY_DISPLAY_NAME: {
    CONTROL: 'CTRL',
    ESCAPE: 'ESC',
    META: 'CMD',
    SHIFT: 'Shift',
  },
  LOCALES: {
    EN: 'en',
    CS_CZ: 'cs_cz',

    DEFAULT: 'en',
  },
  MAX_ALLOWED_UNITS: 99,
  SIGN_IN_FORM_WIDTH: 300,

  /**
   * Constants for maxToRenderPerBatch parameter that is used for FlatList or SectionList. This controls the amount of items rendered per batch, which is the next chunk of items
   * rendered on every scroll.
   */
  MAX_TO_RENDER_PER_BATCH: {
    DEFAULT: 5,
    CAROUSEL: 3,
  },

  /**
   * The maximum count of items per page for SelectionList.
   * When paginate, it multiplies by page number.
   */
  MAX_SELECTION_LIST_PAGE_LENGTH: 500,

  // These split the maximum decimal value of a signed 64-bit number (9,223,372,036,854,775,807) into parts where none of them are too big to fit into a 32-bit number, so that we can
  // generate them each with a random number generator with only 32-bits of precision.
  MAX_64BIT_LEFT_PART: 92233,
  MAX_64BIT_MIDDLE_PART: 7203685,
  MAX_64BIT_RIGHT_PART: 4775807,

  EMAIL_MAX_LENGTH: 254,

  // When generating a random value to fit in 7 digits (for the `middle` or `right` parts above), this is the maximum value to multiply by Math.random().
  MAX_INT_FOR_RANDOM_7_DIGIT_VALUE: 10000000,
  MERGED_ACCOUNT_PREFIX: 'MERGED_',
  MODAL: {
    MODAL_TYPE: {
      CONFIRM: 'confirm',
      CENTERED: 'centered',
      CENTERED_UNSWIPEABLE: 'centered_unswipeable',
      CENTERED_SMALL: 'centered_small',
      BOTTOM_DOCKED: 'bottom_docked',
      POPOVER: 'popover',
      RIGHT_DOCKED: 'right_docked',
      ONBOARDING: 'onboarding',
    },
    ANCHOR_ORIGIN_VERTICAL: {
      TOP: 'top',
      CENTER: 'center',
      BOTTOM: 'bottom',
    },
    ANCHOR_ORIGIN_HORIZONTAL: {
      LEFT: 'left',
      CENTER: 'center',
      RIGHT: 'right',
    },
    POPOVER_MENU_PADDING: 8,
    RESTORE_FOCUS_TYPE: {
      DEFAULT: 'default',
      DELETE: 'delete',
      PRESERVE: 'preserve',
    },
    ANIMATION_TIMING: {
      DEFAULT_IN: 300,
      DEFAULT_OUT: 200,
      DEFAULT_RIGHT_DOCKED_IOS_IN: 500,
      DEFAULT_RIGHT_DOCKED_IOS_OUT: 400,
      FAB_IN: 350,
      FAB_OUT: 200,
    },
  },
  MONTHS: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  MONTHS_ABBREVIATED: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
  NAVIGATION: {
    TYPE: {
      FORCED_UP: 'FORCED_UP',
      UP: 'UP',
    },
    ACTION_TYPE: {
      REPLACE: 'REPLACE',
      PUSH: 'PUSH',
      NAVIGATE: 'NAVIGATE',
    },
    SESSION_ACTION: {
      SAVE: 'SAVE',
      DISCARD: 'DISCARD',
      BACK: 'BACK',
    },
  },

  ERROR_TYPE: {
    SOCKET: 'Kiroku\\Auth\\Error\\Socket',
  },
  ERROR_TITLE: {
    SOCKET: 'Issue connecting to database',
    DUPLICATE_RECORD: '400 Unique Constraints Violation',
  },
  FORMS: {
    // Forms to load upon onyx initialization
    INITIAL_FORM: 'InitialForm',
    AUTH_FORM: 'AuthForm',
    FORGOT_PASSWORD_FORM: 'ForgotPasswordForm',
    // VALIDATE_CODE_FORM: 'ValidateCodeForm',
    // VALIDATE_TFA_CODE_FORM: 'ValidateTfaCodeForm',
    // RESEND_VALIDATION_FORM: 'ResendValidationForm',
    // RESEND_VALIDATE_CODE_FORM: 'ResendValidateCodeForm',
    // ...
  },

  BOOT_SPLASH_STATE: {
    VISIBLE: 'visible',
    READY_TO_BE_HIDDEN: 'readyToBeHidden',
    HIDDEN: `hidden`,
  },

  // Cap how long the boot splash waits for the authed user's RTDB data
  // (userData + preferences) to hydrate. Past this point, splash hides
  // and HomeScreen falls back to its skeleton state. Without the cap an
  // incomplete RTDB document, slow network, or offline cold start would
  // keep the splash up until the 15 s force-hide safety net in
  // SplashScreenHider, which is poor UX. 3 s is comfortably longer than
  // a warm listener (typically <500 ms) and shorter than the safety net.
  BOOT_SPLASH_AUTH_DATA_TIMEOUT_MS: 3 * 1000,

  // Cap how long the boot waits for Onyx schema migrations (`migrateOnyx`).
  // The migration step gates the navigator, so a migration that hangs on a
  // slow or corrupt store would otherwise pin the splash until the 15 s
  // force-hide net dissolves it over an empty tree. Past this point the app
  // boots anyway: the migrations keep running in the background, and because
  // the schema version is stamped only on completion, the next launch retries
  // whatever didn't finish. 8 s is far longer than a real pass (a few hundred
  // ms even on a large store) and comfortably under the safety net.
  BOOT_SPLASH_MIGRATION_TIMEOUT_MS: 8 * 1000,

  // Last-resort safety net (SplashScreenHider): force-hide the splash if the
  // gates never resolve, so a stuck boot can't pin the (untappable) overlay.
  BOOT_SPLASH_FORCE_HIDE_TIMEOUT_MS: 15 * 1000,
  // Kiroku logs a full gate snapshot if the splash is still up this long, to
  // name the offender. Must stay BELOW BOOT_SPLASH_FORCE_HIDE_TIMEOUT_MS so it
  // fires while still stuck, just before the net hides the splash.
  BOOT_SPLASH_STUCK_LOG_TIMEOUT_MS: 14 * 1000,

  KEYBOARD_TYPE: {
    VISIBLE_PASSWORD: 'visible-password',
    ASCII_CAPABLE: 'ascii-capable',
    NUMBER_PAD: 'number-pad',
    DECIMAL_PAD: 'decimal-pad',
  },

  INPUT_MODE: {
    NONE: 'none',
    TEXT: 'text',
    DECIMAL: 'decimal',
    NUMERIC: 'numeric',
    TEL: 'tel',
    SEARCH: 'search',
    EMAIL: 'email',
    URL: 'url',
  },

  VERIFY_EMAIL: {
    // How often a user can request a new verification email
    COOLDOWN: 60 * 1000, // 1 minute
  },

  SESSION_STORAGE_KEYS: {
    INITIAL_URL: 'INITIAL_URL',
    ACTIVE_WORKSPACE_ID: 'ACTIVE_WORKSPACE_ID',
    RETRY_LAZY_REFRESHED: 'RETRY_LAZY_REFRESHED',
  },

  NETWORK: {
    METHOD: {
      POST: 'post',
    },
    MIN_RETRY_WAIT_TIME_MS: 10,
    MAX_RANDOM_RETRY_WAIT_TIME_MS: 100,
    MAX_RETRY_WAIT_TIME_MS: 10 * 1000,
    PROCESS_REQUEST_DELAY_MS: 1000,
    MAX_PENDING_TIME_MS: 10 * 1000,
    RECHECK_INTERVAL_MS: 60 * 1000,
    MAX_REQUEST_RETRIES: 10,
    // Once the request queue has dropped a live-session flush
    // MAX_LIVE_FLUSH_DROPS times in a row (see `DrinkingSession`), the
    // automatic re-arm slows down to one flush per cooldown, doubling from the
    // first value up to the second, instead of stopping until the next edit.
    LIVE_FLUSH_DROP_COOLDOWN_MS: 30 * 1000,
    LIVE_FLUSH_DROP_COOLDOWN_MAX_MS: 5 * 60 * 1000,
    // Upper bound on how long a kiroku-api request will wait for the Firebase ID
    // token before failing fast. `getIdToken()` is instant while the cached
    // token is valid, but a near-expiry refresh has no built-in timeout and can
    // stall across a connectivity transition, freezing every request until it
    // settles. See `HttpUtils.getFirebaseIdToken`.
    ID_TOKEN_TIMEOUT_MS: 15 * 1000,
    // Header carrying a write's idempotency key. kiroku-api answers a repeat
    // of a key it already applied from its record instead of applying the
    // write again. See `API.write` and kiroku-api `middleware/idempotency.ts`.
    IDEMPOTENCY_KEY_HEADER: 'Idempotency-Key',
    // kiroku-api's clock in epoch ms on every response, for the server-time
    // offset (see `HttpUtils` and `DateUtils.getServerTime`).
    SERVER_TIME_HEADER: 'X-Server-Time',
    NETWORK_STATUS: {
      ONLINE: 'online',
      OFFLINE: 'offline',
      UNKNOWN: 'unknown',
    },
  },
  DEFAULT_TIME_ZONE: {
    // Resolved through `TimezoneUtils` rather than straight off `Intl`: this
    // runs at import time, and a device zone the engine will not accept back
    // (e.g. "GMT" on Hermes/Apple) used to kill the runtime before the first
    // render. See the module header in `src/libs/TimezoneUtils.ts`.
    automatic: true,
    selected: getDeviceTimezone(),
  },
  DEFAULT_ACCOUNT_DATA: {errors: null, success: '', isLoading: false},
  DEFAULT_NETWORK_DATA: {isOffline: false},
  DEFAULT_CLOSE_ACCOUNT_DATA: {errors: null, success: '', isLoading: false},

  AUTH_PROVIDER: {
    PASSWORD: 'password',
    GOOGLE: 'google.com',
    APPLE: 'apple.com',
  } as const,

  MICROSECONDS_PER_MS: 1000,
  REPORT: {
    // Why the user is reporting another user. These values are the wire contract
    // shared with kiroku-api (the server stores them verbatim for admin review),
    // so they must not be renamed without a coordinated backend change.
    REASON: {
      INAPPROPRIATE_NAME: 'inappropriate_name',
      INAPPROPRIATE_PHOTO: 'inappropriate_photo',
      HARASSMENT: 'harassment',
      // One session photo, named by its object path. Offered only when a
      // report is filed about a specific photo (RFC §9).
      PHOTO: 'photo',
      OTHER: 'other',
    },
  },
  RED_BRICK_ROAD_PENDING_ACTION: {
    ADD: 'add',
    DELETE: 'delete',
    UPDATE: 'update',
  },
  BRICK_ROAD_INDICATOR_STATUS: {
    ERROR: 'error',
    INFO: 'info',
  },
  // Image kinds for the kiroku-api image pipeline (`/v1/images/*`, Kiroku #1059).
  // `avatar` is public-read; `session` stays private and is read through
  // short-lived signed urls.
  IMAGE_UPLOAD_KIND: {
    AVATAR: 'avatar',
    SESSION: 'session',
  },
  // How each image kind is prepared before upload. An avatar is a small square
  // thumbnail shown at one size; a session photo is looked at, so it keeps the
  // frame it was shot in (no forced crop) and is resized to a width a phone
  // screen can actually use. Both stay well inside the pipeline's 5 MB cap.
  IMAGE_UPLOAD_PROFILE: {
    avatar: {
      // Square, cropped to fit.
      ASPECT_RATIO: [1, 1],
      OUTPUT_WIDTH: 300,
      QUALITY: 0.8,
    },
    session: {
      // `null` means "keep the source framing": a photo of the night is not a
      // thumbnail, and cropping it to a fixed ratio throws away what it shows.
      ASPECT_RATIO: null,
      OUTPUT_WIDTH: 1080,
      QUALITY: 0.7,
    },
  },
  // Max photos on one session (RFC §9), mirrored by `MAX_SESSION_PHOTOS` in
  // kiroku-api, which is the enforcing side. Held here so the UI can hide the
  // add action instead of letting an upload fail at finalize.
  SESSION_PHOTO_LIMIT: 20,
  ONYX_UPDATE_TYPES: {
    HTTPS: 'https',
    PUSHER: 'pusher',
    AIRSHIP: 'airship',
  },
  OPTION_MODE: {
    COMPACT: 'compact',
    DEFAULT: 'default',
  },
  OS: {
    WINDOWS: 'Windows',
    MAC_OS: 'Mac OS',
    ANDROID: 'Android',
    IOS: 'iOS',
    LINUX: 'Linux',
    NATIVE: 'Native',
  },
  PLATFORM: {
    IOS: 'iOS',
    ANDROID: 'Android',
    WEB: 'Web',
  },
  // Values for checking if polyfill is required on a platform
  POLYFILL_TEST: {
    STYLE: 'currency',
    CURRENCY: 'XAF',
    FORMAT: 'symbol',
    SAMPLE_INPUT: '123456.789',
    EXPECTED_OUTPUT: 'FCFA 123,457',
  },
  PULL_REQUEST_NUMBER,
  PUSHER: {
    // kiroku-api authorizes/publishes to plain `private-user-<uid>` channels.
    PRIVATE_USER_CHANNEL_PREFIX: 'private-user-',
    // Public (unauthenticated) channel carrying global app-config broadcasts.
    CONFIG_CHANNEL: 'config',
    CONFIG_UPDATE_EVENT: 'configUpdate',
  },
  REASON_FOR_LEAVING: {
    DB_KEY_LENGTH: 32,
  },

  REGEX: {
    SPECIAL_CHARS_WITHOUT_NEWLINE: /((?!\n)[()-\s\t])/g,
    DIGITS_AND_PLUS: /^\+?[0-9]*$/,
    ALPHABETIC_AND_LATIN_CHARS: /^[\p{Script=Latin} ]*$/u,
    NON_ALPHABETIC_AND_NON_LATIN_CHARS: /[^\p{Script=Latin}]/gu,
    ACCENT_LATIN_CHARS: /[\u00C0-\u017F]/g,
    INVISIBLE_CHARACTERS_GROUPS: /[\p{C}\p{Z}]/gu,
    EMAIL:
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    OTHER_INVISIBLE_CHARACTERS: /[\u3164]/g,
    POSITIVE_INTEGER: /^\d+$/,
    ROUTES: {
      REDUNDANT_SLASHES: /(\/{2,})|(\/$)/g,
    },
    NON_NUMERIC: /\D/g,
    CODE_2FA: /^\d{6}$/,
    NUMBER: /^[0-9]+$/,
    CARD_NUMBER: /^[0-9]{15,16}$/,
    CARD_SECURITY_CODE: /^[0-9]{3,4}$/,
    CARD_EXPIRATION_DATE: /^(0[1-9]|1[0-2])([^0-9])?([0-9]{4}|([0-9]{2}))$/,
    TIME_FORMAT: /^\d{2}:\d{2} [AP]M$/,
    DATE_TIME_FORMAT: /^\d{2}-\d{2} \d{2}:\d{2} [AP]M$/,
    YEAR_DATE_TIME_FORMAT: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2} [AP]M$/,
    // at least 8 characters, 1 capital letter, 1 lowercase number, 1 number
    COMPLEX_PASSWORD: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{8,}$/,
  },

  /**
   * Acceptable values for the `role` attribute on react native components.
   *
   * **IMPORTANT:** Not for use with the `accessibilityRole` prop, as it accepts different values, and new components
   * should use the `role` prop instead.
   */
  ROLE: {
    /** Use for elements with important, time-sensitive information. */
    ALERT: 'alert',
    /** Use for elements that act as buttons. */
    BUTTON: 'button',
    /** Use for elements representing checkboxes. */
    CHECKBOX: 'checkbox',
    /** Use for elements that allow a choice from multiple options. */
    COMBOBOX: 'combobox',
    /** Use with scrollable lists to represent a grid layout. */
    GRID: 'grid',
    /** Use for section headers or titles. */
    HEADING: 'heading',
    /** Use for image elements. */
    IMG: 'img',
    /** Use for elements that navigate to other pages or content. */
    LINK: 'link',
    /** Use to identify a list of items. */
    LIST: 'list',
    /** Use for a list of choices or options. */
    MENU: 'menu',
    /** Use for a container of multiple menus. */
    MENUBAR: 'menubar',
    /** Use for items within a menu. */
    MENUITEM: 'menuitem',
    /** Use when no specific role is needed. */
    NONE: 'none',
    /** Use for elements that don't require a specific role. */
    PRESENTATION: 'presentation',
    /** Use for elements showing progress of a task. */
    PROGRESSBAR: 'progressbar',
    /** Use for radio buttons. */
    RADIO: 'radio',
    /** Use for groups of radio buttons. */
    RADIOGROUP: 'radiogroup',
    /** Use for scrollbar elements. */
    SCROLLBAR: 'scrollbar',
    /** Use for text fields that are used for searching. */
    SEARCHBOX: 'searchbox',
    /** Use for adjustable elements like sliders. */
    SLIDER: 'slider',
    /** Use for a button that opens a list of choices. */
    SPINBUTTON: 'spinbutton',
    /** Use for elements providing a summary of app conditions. */
    SUMMARY: 'summary',
    /** Use for on/off switch elements. */
    SWITCH: 'switch',
    /** Use for tab elements in a tab list. */
    TAB: 'tab',
    /** Use for a list of tabs. */
    TABLIST: 'tablist',
    /** Use for text elements */
    TEXT: 'text',
    /** Use for timer elements. */
    TIMER: 'timer',
    /** Use for toolbars containing action buttons or components. */
    TOOLBAR: 'toolbar',
  },

  SCREEN_READER_STATES: {
    ALL: 'all',
    ACTIVE: 'active',
    DISABLED: 'disabled',
  },
  SELECTION_SCRAPER_HIDDEN_ELEMENT: 'selection-scrapper-hidden-element',
  SWIPE_DIRECTION: {
    DOWN: 'down',
    LEFT: 'left',
    RIGHT: 'right',
    UP: 'up',
  },
  SESSION_EXPIRY: 60 * 60 * 1000 * 12, // 12 hours
  SESSION: {
    TYPES: SESSION_TYPES,
    REALTIME: [SESSION_TYPES.LIVE],
    // Sessions v2 (docs/sessions-v2.md §4). A session carrying this
    // `schema_version` stores its drinks as `entries`; a session without it is
    // a legacy `drinks[timestamp][drinkKey]` session read through the adapter.
    SCHEMA_VERSION: 2,
    VISIBILITY: {
      FRIENDS: 'friends',
      PRIVATE: 'private',
    },
    // Where an entry was logged from (RFC §4.3 `source`).
    ENTRY_SOURCE: {
      PHONE: 'phone',
      WATCH: 'watch',
      LIVE_ACTIVITY: 'live_activity',
      WEB: 'web',
    },
    // `target_uid` of an entry logged for nobody in particular (RFC §4.3).
    ENTRY_TARGET_UNCLAIMED: 'unclaimed',
    MEMBER_ROLE: {
      ADMIN: 'admin',
      MEMBER: 'member',
    },
    MEMBER_STATUS: {
      INVITED: 'invited',
      ACTIVE: 'active',
      LEFT: 'left',
      REMOVED: 'removed',
    },
    JOINED_VIA: {
      INVITE: 'invite',
      LINK: 'link',
      CODE: 'code',
    },
    // The local hour each part of the day starts at, for the default session
    // name ("Friday evening", RFC §9). Night runs from 22:00 to 04:59.
    PART_OF_DAY_START: {
      MORNING: 5,
      AFTERNOON: 12,
      EVENING: 17,
      NIGHT: 22,
    },
  },
  SOCIALS: {
    TWITTER: '',
    INSTAGRAM: '',
    FACEBOOK: '',
    LINKEDIN: '',
  },
  SPACE_CHARACTER_WIDTH: 4,
  STATUS_BAR_STYLE: {
    LIGHT_CONTENT: 'light-content',
    DARK_CONTENT: 'dark-content',
  },
  STORE_LINKS: {
    ANDROID: `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`,
    // The App Store listing (com.kiroku.app, app id 6670502234). Update and
    // force-update prompts open this, so it must point at the live store app.
    IOS: 'https://apps.apple.com/app/id6670502234',
    DESKTOP: '',
  },
  TOOLTIP_MAX_LINES: 3,
  THEME: {
    DEFAULT: 'light',
    FALLBACK: 'dark',
    DARK: 'dark',
    LIGHT: 'light',
    SYSTEM: 'system',
  },
  TEST_TOOL: {
    // Number of concurrent taps to open then the Test modal menu
    NUMBER_OF_TAPS: 4,
  },
  TIMING: {
    HOMEPAGE_INITIAL_RENDER: 'homepage_initial_render',
    SEARCH_RENDER: 'search_render',
    COLD: 'cold',
    WARM: 'warm',
    COMMENT_LENGTH_DEBOUNCE_TIME: 500,
    RESIZE_DEBOUNCE_TIME: 100,
    SEARCH_OPTION_LIST_DEBOUNCE_TIME: 300,
    TOOLTIP_SENSE: 1000,
    TEST_TOOLS_MODAL_THROTTLE_TIME: 800,
    /** How long the friend list's first-open-of-run cold gate may hold
     *  skeletons while waiting for the live sync before falling back to the
     *  persisted cache (slow networks shouldn't hide data we already have). */
    FRIENDS_COLD_SYNC_TIMEOUT: 4000,
  },
  TIME_PERIOD: {
    AM: 'AM',
    PM: 'PM',
  },
  INDENTS: '    ',
  TWO_FACTOR_AUTH_STEPS: {
    CODES: 'CODES',
    VERIFY: 'VERIFY',
    SUCCESS: 'SUCCESS',
    ENABLED: 'ENABLED',
    DISABLED: 'DISABLED',
  },
  DRINKS: {
    // Perhaps move to types?
    KEYS: {
      SMALL_BEER: 'small_beer',
      BEER: 'beer',
      COCKTAIL: 'cocktail',
      OTHER: 'other',
      STRONG_SHOT: 'strong_shot',
      WEAK_SHOT: 'weak_shot',
      WINE: 'wine',
    },
    ACTIONS: {
      ADD: 'add',
      REMOVE: 'remove',
    },
  },

  // Per-key serving-size + ABV defaults used when a DrinkEntry does not carry
  // explicit `volume_ml` / `abv` overrides. SDU math falls back to these so the
  // legacy numeric form produces the same result as an entry stamped with
  // these values. Keyed by `CONST.DRINKS.KEYS` string values.
  DRINK_DEFAULTS: {
    small_beer: {ml: 330, abv: 0.05},
    beer: {ml: 500, abv: 0.05},
    wine: {ml: 150, abv: 0.12},
    cocktail: {ml: 250, abv: 0.1},
    strong_shot: {ml: 40, abv: 0.4},
    weak_shot: {ml: 40, abv: 0.2},
    other: {ml: 200, abv: 0.1},
  },

  GENDER: {
    MALE: 'male',
    FEMALE: 'female',
    OTHER: 'other',
  },

  // 6 numeric digits
  VALIDATE_CODE_REGEX_STRING: /^\d{6}$/,

  // 8 alphanumeric characters
  RECOVERY_CODE_REGEX_STRING: /^[a-zA-Z0-9]{8}$/,

  UNICODE: {
    LTR: '\u2066',
  },

  // The server has a WAF (Web Application Firewall) which will strip out HTML/XML tags using this regex pattern.
  // It's copied here so that the same regex pattern can be used in form validations to be consistent with the server.
  VALIDATE_FOR_HTML_TAG_REGEX: /<([^>\s]+)(?:[^>]*?)>/g,

  VALIDATE_FOR_LEADINGSPACES_HTML_TAG_REGEX: /<([\s]+.+[\s]*)>/g,

  VIDEO_PLAYER: {
    POPOVER_Y_OFFSET: -30,
    PLAYBACK_SPEEDS: [0.25, 0.5, 1, 1.5, 2],
    HIDE_TIME_TEXT_WIDTH: 250,
    MIN_WIDTH: 170,
    MIN_HEIGHT: 120,
    CONTROLS_POSITION: {
      NATIVE: 32,
      NORMAL: 8,
    },
    DEFAULT_VIDEO_DIMENSIONS: {width: 1900, height: 1400},
  },

  WHITELISTED_TAGS: [/<>/, /< >/, /<->/, /<-->/, /<br>/, /<br\/>/],

  /**
   * native IDs for close buttons in Overlay component
   */
  OVERLAY: {
    TOP_BUTTON_NATIVE_ID: 'overLayTopButton',
    BOTTOM_BUTTON_NATIVE_ID: 'overLayBottomButton',
  },

  BACK_BUTTON_NATIVE_ID: 'backButton',

  WEEK_STARTS_ON: 1, // Monday

  ALL_COUNTRIES: {
    AF: 'Afghanistan',
    AX: 'Åland Islands',
    AL: 'Albania',
    DZ: 'Algeria',
    AS: 'American Samoa',
    AD: 'Andorra',
    AO: 'Angola',
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AG: 'Antigua & Barbuda',
    AR: 'Argentina',
    AM: 'Armenia',
    AW: 'Aruba',
    AC: 'Ascension Island',
    AU: 'Australia',
    AT: 'Austria',
    AZ: 'Azerbaijan',
    BS: 'Bahamas',
    BH: 'Bahrain',
    BD: 'Bangladesh',
    BB: 'Barbados',
    BY: 'Belarus',
    BE: 'Belgium',
    BZ: 'Belize',
    BJ: 'Benin',
    BM: 'Bermuda',
    BT: 'Bhutan',
    BO: 'Bolivia',
    BA: 'Bosnia & Herzegovina',
    BW: 'Botswana',
    BR: 'Brazil',
    IO: 'British Indian Ocean Territory',
    VG: 'British Virgin Islands',
    BN: 'Brunei',
    BG: 'Bulgaria',
    BF: 'Burkina Faso',
    BI: 'Burundi',
    KH: 'Cambodia',
    CM: 'Cameroon',
    CA: 'Canada',
    CV: 'Cape Verde',
    BQ: 'Caribbean Netherlands',
    KY: 'Cayman Islands',
    CF: 'Central African Republic',
    TD: 'Chad',
    CL: 'Chile',
    CN: 'China',
    CX: 'Christmas Island',
    CC: 'Cocos (Keeling) Islands',
    CO: 'Colombia',
    KM: 'Comoros',
    CG: 'Congo - Brazzaville',
    CD: 'Congo - Kinshasa',
    CK: 'Cook Islands',
    CR: 'Costa Rica',
    CI: "Côte d'Ivoire",
    HR: 'Croatia',
    CU: 'Cuba',
    CW: 'Curaçao',
    CY: 'Cyprus',
    CZ: 'Czech Republic',
    DK: 'Denmark',
    DJ: 'Djibouti',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    EC: 'Ecuador',
    EG: 'Egypt',
    SV: 'El Salvador',
    GQ: 'Equatorial Guinea',
    ER: 'Eritrea',
    EE: 'Estonia',
    ET: 'Ethiopia',
    FK: 'Falkland Islands',
    FO: 'Faroe Islands',
    FJ: 'Fiji',
    FI: 'Finland',
    FR: 'France',
    GF: 'French Guiana',
    PF: 'French Polynesia',
    TF: 'French Southern Territories',
    GA: 'Gabon',
    GM: 'Gambia',
    GE: 'Georgia',
    DE: 'Germany',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GR: 'Greece',
    GL: 'Greenland',
    GD: 'Grenada',
    GP: 'Guadeloupe',
    GU: 'Guam',
    GT: 'Guatemala',
    GG: 'Guernsey',
    GN: 'Guinea',
    GW: 'Guinea-Bissau',
    GY: 'Guyana',
    HT: 'Haiti',
    HN: 'Honduras',
    HK: 'Hong Kong',
    HU: 'Hungary',
    IS: 'Iceland',
    IN: 'India',
    ID: 'Indonesia',
    IR: 'Iran',
    IQ: 'Iraq',
    IE: 'Ireland',
    IM: 'Isle of Man',
    IL: 'Israel',
    IT: 'Italy',
    JM: 'Jamaica',
    JP: 'Japan',
    JE: 'Jersey',
    JO: 'Jordan',
    KZ: 'Kazakhstan',
    KE: 'Kenya',
    KI: 'Kiribati',
    XK: 'Kosovo',
    KW: 'Kuwait',
    KG: 'Kyrgyzstan',
    LA: 'Laos',
    LV: 'Latvia',
    LB: 'Lebanon',
    LS: 'Lesotho',
    LR: 'Liberia',
    LY: 'Libya',
    LI: 'Liechtenstein',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    MO: 'Macau',
    MK: 'Macedonia',
    MG: 'Madagascar',
    MW: 'Malawi',
    MY: 'Malaysia',
    MV: 'Maldives',
    ML: 'Mali',
    MT: 'Malta',
    MH: 'Marshall Islands',
    MQ: 'Martinique',
    MR: 'Mauritania',
    MU: 'Mauritius',
    YT: 'Mayotte',
    MX: 'Mexico',
    FM: 'Micronesia',
    MD: 'Moldova',
    MC: 'Monaco',
    MN: 'Mongolia',
    ME: 'Montenegro',
    MS: 'Montserrat',
    MA: 'Morocco',
    MZ: 'Mozambique',
    MM: 'Myanmar (Burma)',
    NA: 'Namibia',
    NR: 'Nauru',
    NP: 'Nepal',
    NL: 'Netherlands',
    NC: 'New Caledonia',
    NZ: 'New Zealand',
    NI: 'Nicaragua',
    NE: 'Niger',
    NG: 'Nigeria',
    NU: 'Niue',
    NF: 'Norfolk Island',
    KP: 'North Korea',
    MP: 'Northern Mariana Islands',
    NO: 'Norway',
    OM: 'Oman',
    PK: 'Pakistan',
    PW: 'Palau',
    PS: 'Palestinian Territories',
    PA: 'Panama',
    PG: 'Papua New Guinea',
    PY: 'Paraguay',
    PE: 'Peru',
    PH: 'Philippines',
    PN: 'Pitcairn Islands',
    PL: 'Poland',
    PT: 'Portugal',
    PR: 'Puerto Rico',
    QA: 'Qatar',
    RE: 'Réunion',
    RO: 'Romania',
    RU: 'Russia',
    RW: 'Rwanda',
    BL: 'Saint Barthélemy',
    WS: 'Samoa',
    SM: 'San Marino',
    ST: 'São Tomé & Príncipe',
    SA: 'Saudi Arabia',
    SN: 'Senegal',
    RS: 'Serbia',
    SC: 'Seychelles',
    SL: 'Sierra Leone',
    SG: 'Singapore',
    SX: 'Sint Maarten',
    SK: 'Slovakia',
    SI: 'Slovenia',
    SB: 'Solomon Islands',
    SO: 'Somalia',
    ZA: 'South Africa',
    GS: 'South Georgia & South Sandwich Islands',
    KR: 'South Korea',
    SS: 'South Sudan',
    ES: 'Spain',
    LK: 'Sri Lanka',
    SH: 'St. Helena',
    KN: 'St. Kitts & Nevis',
    LC: 'St. Lucia',
    MF: 'St. Martin',
    PM: 'St. Pierre & Miquelon',
    VC: 'St. Vincent & Grenadines',
    SD: 'Sudan',
    SR: 'Suriname',
    SJ: 'Svalbard & Jan Mayen',
    SZ: 'Swaziland',
    SE: 'Sweden',
    CH: 'Switzerland',
    SY: 'Syria',
    TW: 'Taiwan',
    TJ: 'Tajikistan',
    TZ: 'Tanzania',
    TH: 'Thailand',
    TL: 'Timor-Leste',
    TG: 'Togo',
    TK: 'Tokelau',
    TO: 'Tonga',
    TT: 'Trinidad & Tobago',
    TA: 'Tristan da Cunha',
    TN: 'Tunisia',
    TR: 'Turkey',
    TM: 'Turkmenistan',
    TC: 'Turks & Caicos Islands',
    TV: 'Tuvalu',
    UM: 'U.S. Outlying Islands',
    VI: 'U.S. Virgin Islands',
    UG: 'Uganda',
    UA: 'Ukraine',
    AE: 'United Arab Emirates',
    GB: 'United Kingdom',
    US: 'United States',
    UY: 'Uruguay',
    UZ: 'Uzbekistan',
    VU: 'Vanuatu',
    VA: 'Vatican City',
    VE: 'Venezuela',
    VN: 'Vietnam',
    WF: 'Wallis & Futuna',
    EH: 'Western Sahara',
    YE: 'Yemen',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
  },
} as const;

type Country = keyof typeof CONST.ALL_COUNTRIES;

export type {Country};

export default CONST;
